import { Box, Group, Text, Flex, Loader } from "@mantine/core";
import {
  CustomTable,
  CustomColumnFilter,
  CustomColumnSorter,
  CustomPagination,
} from "@components/custom";
import { HeaderGroup, RowModel, flexRender } from "@tanstack/react-table";
import React from "react";

import Style from "@scss/custom-components/datatable.module.scss";

export interface CustomDataTableState {
  isLoading?: boolean;
  error?: boolean | string;
  hasData?: boolean;
}

export interface CustomDataTableMessages {
  loading?: string;
  error?: string;
  empty?: string;
}

export const CustomDataTable: React.FC<{
  variant?: "default" | "no-background";
  pagination?: boolean;
  getHeaderGroups: () => HeaderGroup<any>[];
  getRowModel: () => RowModel<any>;
  pageCount: number;
  setCurrent?: (current: number) => void;
  current: number;
  onRowClick?: (row: any) => void;
  getRowClassName?: (row: any) => string;
  state?: CustomDataTableState;
  messages?: CustomDataTableMessages;
  loadingMessage?: string;
  errorMessage?: string;
  emptyMessage?: string;
  paginationPaddingX?: number | string;
  paginationShowPageSizeSelector?: boolean;
  paginationPageSizeOptions?: number[];
  paginationSelectVariant?: "default" | "dark" | "darker" | "sky";
  height?: string | number;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
}> = ({
  getHeaderGroups,
  getRowModel,
  pageCount,
  setCurrent,
  variant = "default",
  pagination = true,
  onRowClick,
  paginationPaddingX = 0,
  paginationShowPageSizeSelector = true,
  paginationPageSizeOptions = [5, 10, 25, 50],
  paginationSelectVariant = "darker",
  getRowClassName,
  state,
  loadingMessage = "Loading data...",
  errorMessage = "Error loading data.",
  emptyMessage = "No data found.",
  height,
  pageSize,
  onPageSizeChange,
}) => {
  // Auto-detect loading/error states if not provided
  const rowData = getRowModel().rows;
  const hasData = rowData.length > 0;

  // Use provided state or auto-detect
  const isLoading = state?.isLoading ?? false;
  const error = state?.error ?? false;

  // Determine if we should show data
  const shouldShowTable = !isLoading && !error;
  const shouldShowEmptyState = !isLoading && !error && !hasData;

  return (
    <>
      {shouldShowTable && (
        <CustomTable variant={variant} mb="md" highlightOnHover height={height}>
          <thead>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th key={header.id}>
                      {!header.isPlaceholder && (
                        <Group gap="xs">
                          <Box
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent:
                                header.column.columnDef.meta?.align === "right"
                                  ? "flex-end"
                                  : header.column.columnDef.meta?.align ===
                                      "center"
                                    ? "center"
                                    : "flex-start",
                              alignItems: "center",
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}

                            <Group gap={0}>
                              <CustomColumnSorter column={header.column} />
                              <CustomColumnFilter column={header.column} />
                            </Group>
                          </Box>
                        </Group>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {getRowModel().rows.map((row) => {
              const customClassName = getRowClassName
                ? getRowClassName(row)
                : "";
              return (
                <tr
                  key={row.id}
                  className={customClassName}
                  onClick={() => onRowClick?.(row.original)}
                  style={{
                    cursor: onRowClick ? "pointer" : "default",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!onRowClick) return;
                    e.currentTarget.classList.add(Style.rowHover);
                    e.currentTarget.classList.remove(Style.rowTransparent);
                  }}
                  onMouseLeave={(e) => {
                    if (!onRowClick) return;
                    e.currentTarget.classList.add(Style.rowTransparent);
                    e.currentTarget.classList.remove(Style.rowHover);
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id}>
                        <Box className={Style.cellText}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Box>
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {/* Error State */}
            {error && (
              <tr>
                <td colSpan={100}>
                  <Text className="carrot" fw="bold" my="xs" size="sm">
                    <></>
                    {typeof error === "string" ? error : errorMessage}
                  </Text>
                </td>
              </tr>
            )}

            {/* Loading State */}
            {isLoading && (
              <tr>
                <td colSpan={100}>
                  <Flex m="sm">
                    <Loader size="xs" style={{ marginRight: "8px" }} />
                    <Text className="grey300" fw="bold" my="xs" size="sm">
                      {loadingMessage}
                    </Text>
                  </Flex>
                </td>
              </tr>
            )}

            {/* Empty State */}
            {shouldShowEmptyState && (
              <tr>
                <td colSpan={100}>
                  <Text fw="bold" my="xs" size="sm">
                    <></>
                    {emptyMessage}
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </CustomTable>
      )}

      {shouldShowTable && pagination && (
        <CustomPagination
          mt="md"
          total={pageCount}
          onChange={setCurrent}
          paddingX={paginationPaddingX}
          showPageSizeSelector={paginationShowPageSizeSelector}
          selectVariant={paginationSelectVariant}
          pageSizeOptions={paginationPageSizeOptions}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </>
  );
};
